import type { OperationItem } from '@ops/entities/operation-item';

// Resolve a product title from the i18n JSONB (`[{ text, languageCode,
// default }]`), a `{ en }` object, or a plain string. When `languageCode`
// (the itinerary's language) is given, prefer that entry so the Operations
// list shows the same title as the Planner and the Operations report — the
// Planner resolves by itinerary language and so does the report path
// (`report-row.ts` `reportProductName`); the list card must match (XT-259).
export const getProductTitle = (
  title: unknown,
  languageCode?: string | null
): string => {
  if (typeof title === 'string') return title;
  if (Array.isArray(title)) {
    const entries = title as Array<{
      text?: string | null;
      languageCode?: string | null;
      default?: boolean | null;
    }>;
    // Prefer the itinerary's language, then default, then en, then any.
    const lang = languageCode?.toLowerCase();
    const byLang = lang
      ? entries.find((e) => e.languageCode?.toLowerCase() === lang && e.text)
      : undefined;
    const byDefault = entries.find((e) => e.default && e.text);
    const byEn = entries.find((e) => e.languageCode === 'en' && e.text);
    const first = entries.find((e) => e.text);
    return (byLang ?? byDefault ?? byEn ?? first)?.text ?? '';
  }
  if (title && typeof title === 'object' && 'en' in title)
    return String((title as Record<string, unknown>).en ?? '');
  return '';
};

// Display name for a row: the product title wins; the catalog name is only a
// fallback (it's the org catalog, e.g. "TempoVIP", not the product). Resolves
// the title in the itinerary's language (`languageCode`) so it matches the
// Planner (XT-259).
export const displayTitle = (
  item: Pick<OperationItem, 'title' | 'catalogTitle' | 'languageCode'>
): string =>
  getProductTitle(item.title, item.languageCode) ||
  item.catalogTitle ||
  'Untitled';
